import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { parseYmd } from '../scheduleUtils'
import { HMI_YARD } from '../mapUtils'

const clientCache = new Map()

export function _resetClientCache () {
  clientCache.clear()
}

function snapToMondayYmd (ymd) {
  const d = parseYmd(ymd)
  const dow = d.getDay()
  let offset
  if (dow === 0) offset = 1
  else if (dow === 6) offset = 2
  else offset = 1 - dow
  d.setDate(d.getDate() + offset)
  // Format directly from local-time components to avoid the UTC-positive-TZ
  // off-by-one that `toISOString().slice(0,10)` produces (e.g., Tokyo at
  // UTC+9 would read midnight-local as the previous UTC day).
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function useWeekStops ({ url, focusDate, planningMode, focusCrewId, isFleetMode, availableCrews }) {
  const [stops, setStops] = useState([])
  const [days, setDays] = useState([])
  const [missingDates, setMissingDates] = useState([])
  // [{ crewId, crewName, dates: [{ date, reason: 'empty' | 'error', message? }] }]
  const [missingByCrew, setMissingByCrew] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const reqIdRef = useRef(0)

  // Stable signature of the crew list — avoids re-firing fetch when the
  // parent rebuilds the array without changing its IDs.
  const crewIdsKey = (availableCrews || []).map(c => c.crewId).sort().join(',')

  const fetch = useCallback(async ({ bypassCache } = {}) => {
    if (planningMode !== 'week' && planningMode !== 'two-week') {
      setStops([]); setDays([]); setMissingDates([]); setMissingByCrew([]); return
    }
    if (!focusDate || !availableCrews?.length) return

    const weeks = planningMode === 'two-week' ? 2 : 1
    const mondayYmd = snapToMondayYmd(focusDate)

    // Fleet mode: fan out to every available crew.
    // Single-crew focus: find that crew in the list.
    // Each "request" tracks the source crew so we can attribute stops back to it.
    const requests = isFleetMode
      ? availableCrews.map(c => ({ crew: c, crewKey: c.crewId }))
      : (() => {
        const focused = availableCrews.find(c => String(c.crewId) === String(focusCrewId))
        return focused ? [{ crew: focused, crewKey: focused.crewId }] : []
      })()

    if (!requests.length) {
      setStops([]); setDays([]); setMissingDates([]); setMissingByCrew([]); return
    }

    // Bump request id so a slow earlier request can't overwrite a newer one.
    const myReqId = ++reqIdRef.current
    setLoading(true)
    setError(null)

    try {
      const responses = await Promise.all(requests.map(({ crewKey }) => {
        const cacheKey = `${mondayYmd}:${weeks}:${crewKey}`
        if (!bypassCache && clientCache.has(cacheKey)) {
          return Promise.resolve(clientCache.get(cacheKey))
        }
        const qs = new URLSearchParams({
          weekOf: mondayYmd,
          weeks: String(weeks),
          crewLeaderId: String(crewKey)
        })
        if (bypassCache) qs.set('nocache', '1')
        return axios.get(`${url}/api/aspire/routing/week-stops/v2?${qs}`).then(r => {
          clientCache.set(cacheKey, r.data)
          return r.data
        })
      }))

      if (myReqId !== reqIdRef.current) return  // stale; newer request is in flight

      const allStops = []
      const allDays = []
      const allMissing = new Set()
      const perCrewMissing = []
      for (let i = 0; i < responses.length; i++) {
        const payload = responses[i]
        const sourceCrew = requests[i].crew
        for (const day of payload.days || []) {
          day.crewId = sourceCrew.crewId
          day.crewName = sourceCrew.crewName
          for (const stop of day.stops || []) {
            stop.crewId = sourceCrew.crewId
            stop.crewName = sourceCrew.crewName
            stop.dayIndex = day.dayIndex
            stop.weekType = day.weekType
            stop.date = day.date
            allStops.push(stop)
          }
          allDays.push(day)
        }
        const errorsByDate = payload.meta?.errorsByDate || {}
        const missing = payload.meta?.missingDates || []
        if (missing.length) {
          perCrewMissing.push({
            crewId: sourceCrew.crewId,
            crewName: sourceCrew.crewName,
            dates: missing.map(date => errorsByDate[date]
              ? { date, reason: 'error', message: errorsByDate[date] }
              : { date, reason: 'empty' }
            )
          })
        }
        for (const d of missing) allMissing.add(d)
      }
      setStops(allStops)
      setDays(allDays)
      setMissingDates(Array.from(allMissing))
      setMissingByCrew(perCrewMissing)
    } catch (err) {
      if (err.response?.status === 413) {
        setError('Window too large — reduce the date range and try again')
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load week schedule')
      }
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false)
    }
  }, [url, focusDate, planningMode, focusCrewId, isFleetMode, crewIdsKey])

  useEffect(() => { fetch() }, [fetch])

  const refetch = useCallback(() => {
    // Invalidate both client + server caches.
    const weeks = planningMode === 'two-week' ? 2 : 1
    const mondayYmd = focusDate ? snapToMondayYmd(focusDate) : null
    if (mondayYmd) {
      const crewKeys = isFleetMode
        ? (availableCrews || []).map(c => c.crewId)
        : [String(focusCrewId)]
      crewKeys.forEach(k => clientCache.delete(`${mondayYmd}:${weeks}:${k}`))
    }
    return fetch({ bypassCache: true })
  }, [fetch, focusDate, planningMode, focusCrewId, crewIdsKey])

  return { stops, days, missingDates, missingByCrew, loading, error, refetch, yard: HMI_YARD }
}
